import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AppDispatch, RootState } from '../../../store';
import { setFilter, fetchFilteredArticles, resetFilters } from '../slices/articlesSlice';
import dayjs from 'dayjs';
import DeleteFilteredArticlesButton from './DeleteFilteredArticlesButton';

// Article status options
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'published', label: 'Published' },
  { value: 'test', label: 'Test' },
  { value: 'offline', label: 'Off line' },
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: '1', label: 'High (1)' },
  { value: '2', label: 'Medium (2)' },
  { value: '3', label: 'Low (3)' },
];

const ArticleFilters: React.FC<{ publisherId?: string }> = ({ publisherId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters, isLoading } = useSelector((state: RootState) => state.articles);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Set publisher filter if publisherId is provided
  useEffect(() => {
    if (publisherId) {
      dispatch(setFilter({ key: 'publisherPK', value: `PUBLISHER#${publisherId}` }));
    }
  }, [publisherId, dispatch]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    dispatch(setFilter({ key: 'status', value }));
  };

  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    dispatch(setFilter({ key: 'priority', value }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(setFilter({ key: 'searchTerm', value }));
  };

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
    if (date) {
      const dateString = date.format('YYYY-MM-DD');
      dispatch(setFilter({ key: 'fromDate', value: dateString }));
    } else {
      dispatch(setFilter({ key: 'fromDate', value: '' }));
    }
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
    if (date) {
      const dateString = date.format('YYYY-MM-DD');
      dispatch(setFilter({ key: 'toDate', value: dateString }));
    } else {
      dispatch(setFilter({ key: 'toDate', value: '' }));
    }
  };

  const handleApplyFilters = () => {
    dispatch(fetchFilteredArticles(filters));
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    dispatch(resetFilters());

    // If publisherId is provided, keep that filter after reset
    if (publisherId) {
      dispatch(setFilter({ key: 'publisherPK', value: `PUBLISHER#${publisherId}` }));
    }

    dispatch(fetchFilteredArticles(publisherId ? { publisherPK: `PUBLISHER#${publisherId}` } : {}));
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!expanded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => setExpanded(true)}
            sx={{ minHeight: 40 }}
          >
            Search
          </Button>
        </Box>
      )}
      <Collapse in={expanded}>
        <Grid container spacing={2} alignItems="flex-end">
          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={filters.status || ''}
                label="Status"
                onChange={handleStatusChange}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Priority Filter */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth>
              <InputLabel id="priority-select-label">Priority</InputLabel>
              <Select
                labelId="priority-select-label"
                id="priority-select"
                value={(filters.priority as any) || ''}
                label="Priority"
                onChange={handlePriorityChange}
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Date From */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="From Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            {/* Date To */}
            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="To Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </LocalizationProvider>
          {/* Search */}
          <Grid item xs={12} sm={12} md={2.4}>
            <TextField
              fullWidth
              label="Search Articles"
              variant="outlined"
              value={filters.searchTerm || ''}
              onChange={handleSearchChange}
              placeholder="Search by headline, content..."
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
                sx={{ minHeight: 40 }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{ minHeight: 40 }}
              >
                Reset
              </Button>
              <Box sx={{ display: 'flex' }}>
                <DeleteFilteredArticlesButton disabled={isLoading} />
              </Box>
              <Button
                variant="text"
                color="inherit"
                onClick={() => setExpanded(false)}
                startIcon={<ExpandLessIcon />}
                sx={{ minHeight: 40 }}
              >
                Collapse
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Collapse>
    </Box>
  );
};

export default ArticleFilters;